import React, { useCallback, useEffect, useRef, useState } from 'react';
import './DiagnosticsPage.css';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS = { IDLE: 'idle', OK: 'ok', FAIL: 'fail', CHECKING: 'checking' };
const ICON = { idle: '⋯', ok: '✅', fail: '❌', checking: '⏳' };

const Row = ({ label, status, detail }) => (
  <div className={`diag-row diag-row-${status}`} data-testid={`diag-row-${label.replace(/\s+/g, '-').toLowerCase()}`}>
    <span className="diag-icon">{ICON[status] || '⋯'}</span>
    <span className="diag-label">{label}</span>
    <span className="diag-detail">{detail || ''}</span>
  </div>
);

/**
 * Diagnostics page — users (and admin) can visit this at any time to verify
 * the app is operating correctly. Exposes: API reachability, backend version,
 * lobby WebSocket, service-worker state, cached bundle version, auth state,
 * and a one-click "Reset app & reload" button that nukes all caches + SWs.
 *
 * Designed for zero-dependency operation — imports nothing from contexts so it
 * still works on a broken app (e.g. chat WS failing).
 */
const DiagnosticsPage = ({ onClose }) => {
  const [apiState, setApiState] = useState({ status: STATUS.IDLE });
  const [versionState, setVersionState] = useState({ status: STATUS.IDLE });
  const [wsState, setWsState] = useState({ status: STATUS.IDLE });
  const [authState, setAuthState] = useState({ status: STATUS.IDLE });
  const [swState, setSwState] = useState({ status: STATUS.IDLE });
  const [cacheState, setCacheState] = useState({ status: STATUS.IDLE });
  const [resetting, setResetting] = useState(false);
  const wsRef = useRef(null);

  const checkApi = useCallback(async () => {
    setApiState({ status: STATUS.CHECKING });
    try {
      const r = await fetch(`${API}/api/health`, { cache: 'no-store' });
      if (r.ok) setApiState({ status: STATUS.OK, detail: `${API}` });
      else setApiState({ status: STATUS.FAIL, detail: `HTTP ${r.status}` });
    } catch (e) {
      setApiState({ status: STATUS.FAIL, detail: String(e).slice(0, 80) });
    }
  }, []);

  const checkVersion = useCallback(async () => {
    setVersionState({ status: STATUS.CHECKING });
    try {
      const r = await fetch(`${API}/api/version?t=${Date.now()}`, { cache: 'no-store' });
      const d = await r.json();
      const serverVersion = d.version || 'unknown';
      const cachedVersion = (() => { try { return localStorage.getItem('ckk_app_version'); } catch { return null; } })();
      const match = !cachedVersion || cachedVersion === serverVersion;
      setVersionState({
        status: match ? STATUS.OK : STATUS.FAIL,
        detail: match ? `Server ${serverVersion.slice(0, 19)}` : `Stale — server=${serverVersion.slice(0, 19)}, cached=${cachedVersion?.slice(0, 19)}`
      });
    } catch (e) {
      setVersionState({ status: STATUS.FAIL, detail: String(e).slice(0, 80) });
    }
  }, []);

  const checkAuth = useCallback(async () => {
    setAuthState({ status: STATUS.CHECKING });
    try {
      const r = await fetch(`${API}/api/auth/me`, { credentials: 'include', cache: 'no-store' });
      if (r.status === 200) {
        try {
          const d = await r.json();
          setAuthState({ status: STATUS.OK, detail: `Signed in as ${d.name || d.email || d.user_id}` });
        } catch {
          // Body already consumed by a wrapping monitor (rrweb/emergent-main);
          // the auth endpoint is reachable so treat as OK.
          setAuthState({ status: STATUS.OK, detail: 'Signed in (details unavailable)' });
        }
      } else if (r.status === 401) {
        setAuthState({ status: STATUS.OK, detail: 'Not signed in (OK)' });
      } else {
        setAuthState({ status: STATUS.FAIL, detail: `HTTP ${r.status}` });
      }
    } catch (e) {
      // Some browser extensions / recording scripts wrap fetch and throw
      // "Failed to execute 'clone' on 'Response': Response body is already used"
      // when they race with our own .json() call. That's harmless — the auth
      // endpoint was reached (we saw the 401/200 in the network tab) so don't
      // alarm the user.
      const msg = String(e);
      if (/already used|clone/i.test(msg)) {
        setAuthState({ status: STATUS.OK, detail: 'Auth endpoint reachable (recorder race — harmless)' });
      } else {
        setAuthState({ status: STATUS.FAIL, detail: msg.slice(0, 80) });
      }
    }
  }, []);

  const checkWebSocket = useCallback(async () => {
    setWsState({ status: STATUS.CHECKING });
    try {
      const base = API.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
      const ws = new WebSocket(`${base}/api/ws/lobby`);
      wsRef.current = ws;
      const timeout = setTimeout(() => {
        if (ws.readyState !== WebSocket.OPEN) {
          ws.close();
          setWsState({ status: STATUS.FAIL, detail: 'Connection timeout after 5s' });
        }
      }, 5000);
      ws.onopen = () => {
        clearTimeout(timeout);
        setWsState({ status: STATUS.OK, detail: `Connected to ${base}/api/ws/lobby` });
        ws.close();
      };
      ws.onerror = () => {
        clearTimeout(timeout);
        setWsState({ status: STATUS.FAIL, detail: 'Connection failed — network or server issue' });
      };
    } catch (e) {
      setWsState({ status: STATUS.FAIL, detail: String(e).slice(0, 80) });
    }
  }, []);

  const checkServiceWorker = useCallback(async () => {
    setSwState({ status: STATUS.CHECKING });
    try {
      if (!('serviceWorker' in navigator)) {
        setSwState({ status: STATUS.OK, detail: 'Not supported (OK)' });
        return;
      }
      const regs = await navigator.serviceWorker.getRegistrations();
      if (regs.length === 0) {
        setSwState({ status: STATUS.OK, detail: 'No service workers registered' });
      } else {
        const scripts = regs.map(r => r.active?.scriptURL || r.installing?.scriptURL || 'unknown').join(', ');
        setSwState({ status: STATUS.OK, detail: `${regs.length} registered: ${scripts.split('/').pop()}` });
      }
    } catch (e) {
      setSwState({ status: STATUS.FAIL, detail: String(e).slice(0, 80) });
    }
  }, []);

  const checkCache = useCallback(async () => {
    setCacheState({ status: STATUS.CHECKING });
    try {
      if (typeof caches === 'undefined') {
        setCacheState({ status: STATUS.OK, detail: 'Cache API not available' });
        return;
      }
      const keys = await caches.keys();
      setCacheState({
        status: STATUS.OK,
        detail: keys.length === 0 ? 'No caches' : `${keys.length} cache(s): ${keys.join(', ')}`
      });
    } catch (e) {
      setCacheState({ status: STATUS.FAIL, detail: String(e).slice(0, 80) });
    }
  }, []);

  const runAll = useCallback(async () => {
    await Promise.all([
      checkApi(),
      checkVersion(),
      checkAuth(),
      checkWebSocket(),
      checkServiceWorker(),
      checkCache()
    ]);
  }, [checkApi, checkVersion, checkAuth, checkWebSocket, checkServiceWorker, checkCache]);

  useEffect(() => {
    runAll();
    return () => { if (wsRef.current) try { wsRef.current.close(); } catch {} };
  }, [runAll]);

  const resetApp = useCallback(async () => {
    if (!window.confirm('This will clear all local caches and reload the page. Your login will be preserved. Continue?')) return;
    setResetting(true);
    try {
      if ('serviceWorker' in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister().catch(() => {})));
      }
      if (typeof caches !== 'undefined') {
        const keys = await caches.keys();
        await Promise.all(keys.map(k => caches.delete(k).catch(() => {})));
      }
      try {
        // Preserve auth. Clear only app-state keys.
        const preserve = ['auth_token', 'token'];
        const toClear = Object.keys(localStorage || {}).filter(k => !preserve.includes(k));
        toClear.forEach(k => { try { localStorage.removeItem(k); } catch {} });
      } catch { /* ignore */ }
      try { sessionStorage.clear(); } catch { /* ignore */ }
    } finally {
      // Hard reload
      setTimeout(() => { window.location.reload(); }, 150);
    }
  }, []);

  const allOk = [apiState, versionState, authState, wsState, swState, cacheState]
    .every(s => s.status === STATUS.OK);

  return (
    <div className="diag-overlay" data-testid="diagnostics-page">
      <div className="diag-modal">
        <div className="diag-header">
          <h2>🩺 Game Diagnostics</h2>
          {onClose && (
            <button className="diag-close" onClick={onClose} aria-label="Close" data-testid="diag-close">×</button>
          )}
        </div>

        <p className="diag-intro">
          Use this page to verify the game is operating properly. All checks should show ✅.
          If something fails, click <strong>Reset app &amp; reload</strong> below — this usually fixes stale-bundle issues.
        </p>

        <div className="diag-summary" data-testid="diag-summary">
          {allOk
            ? <span className="diag-summary-ok">✅ All systems operational</span>
            : <span className="diag-summary-warn">⚠️ One or more checks need attention</span>}
        </div>

        <div className="diag-rows">
          <Row label="Backend API"       status={apiState.status}     detail={apiState.detail} />
          <Row label="App version"       status={versionState.status} detail={versionState.detail} />
          <Row label="Authentication"    status={authState.status}    detail={authState.detail} />
          <Row label="Lobby WebSocket"   status={wsState.status}      detail={wsState.detail} />
          <Row label="Service Worker"    status={swState.status}      detail={swState.detail} />
          <Row label="Cache storage"     status={cacheState.status}   detail={cacheState.detail} />
        </div>

        <div className="diag-actions">
          <button
            type="button"
            className="diag-btn diag-btn-secondary"
            onClick={runAll}
            data-testid="diag-run-all"
          >
            Re-run checks
          </button>
          <button
            type="button"
            className="diag-btn diag-btn-danger"
            onClick={resetApp}
            disabled={resetting}
            data-testid="diag-reset"
          >
            {resetting ? 'Resetting…' : 'Reset app & reload'}
          </button>
        </div>

        <div className="diag-footer">
          <small>
            Need help? Copy this whole page and share it with support.<br />
            Browser: <code>{navigator.userAgent.slice(0, 80)}</code>
          </small>
        </div>
      </div>
    </div>
  );
};

export default DiagnosticsPage;
